import { useEffect, useRef, useState } from 'react';
import { ONE_SECOND } from 'src/constants/times.const';

export const useAudioRecorder = ({
  onStop,
  onError,
  maxDurationSec,
}: {
  onStop: (audioBlob: Blob) => void;
  onError: () => void;
  maxDurationSec: number;
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const isStartingRecording = useRef(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const isUnmounted = useRef(false);
  const audioStream = useRef<MediaStream | null>(null);
  const [lastRecording, setLastRecording] = useState<Blob | null>(null);

  const stopRecordingInternal = () => {
    /* istanbul ignore else: should always present */
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
    }
    /* istanbul ignore else: should always present */
    if (audioStream.current) {
      audioStream.current.getTracks().forEach(track => track.stop());
    }
  };

  useEffect(() => {
    isUnmounted.current = false;

    return () => {
      isUnmounted.current = true;
      stopRecordingInternal();
    };
  }, []);

  const handleRecorderStop = async () => {
    setIsRecording(false);

    /* istanbul ignore if: shouldn't happen */
    if (!mediaRecorder.current) return;

    mediaRecorder.current.ondataavailable = () => {};
    mediaRecorder.current.onstop = () => {};

    if (audioChunksRef.current.length === 0 || isUnmounted.current) return;

    const audioBlob = new Blob(audioChunksRef.current);
    audioChunksRef.current = [];
    setLastRecording(audioBlob);
    onStop(audioBlob);
  };

  const startRecording = async () => {
    if (isRecording || isStartingRecording.current) {
      return;
    }

    isStartingRecording.current = true;

    try {
      stopRecordingInternal();

      audioStream.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const recorder = new MediaRecorder(audioStream.current);

      mediaRecorder.current = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }

        // End recording after the first chunk
        // because the chunk duration is set to the audio's maximum duration.
        if (audioChunksRef.current.length > 0) {
          stopRecordingInternal();
        }
      };

      recorder.onstop = handleRecorderStop;

      recorder.start(maxDurationSec * ONE_SECOND);
      setIsRecording(true);
    } catch (err) {
      console.error('Could not start media recording:', err);
      onError();
    } finally {
      isStartingRecording.current = false;
    }
  };

  const stopRecording = () => {
    if (!isRecording) {
      return;
    }

    stopRecordingInternal();
  };

  return {
    isRecording,
    startRecording,
    stopRecording,
    lastRecording,
  };
};
